import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/Card';
import { Input } from '@components/forms/Input';
import { Loader } from '@components/Loader';
import { PublicBookingLayout, StatusIcon } from './PublicBookingLayout';
import {
  PublicApiError,
  PublicBooking,
  PublicBookingPage,
  PublicService,
  PublicSlot,
  formatInTimezone,
  publicBookingService,
} from './publicBookingService';

const SLOT_WINDOW_DAYS = 7;
const CURRENCY = 'EUR';

type PageState = 'loading' | 'ready' | 'unavailable' | 'error';

const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest}min`;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}min`;
};

const addDays = (isoDate: string, days: number): string => {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const SectionTitle: React.FC<{ step: number; children: React.ReactNode }> = ({
  step,
  children,
}) => (
  <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
    <span className="w-6 h-6 rounded-full bg-primary-500 text-white text-xs flex items-center justify-center">
      {step}
    </span>
    {children}
  </h2>
);

const ManageLinkBox: React.FC<{ url: string }> = ({ url }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Clipboard access can be denied; the link stays selectable in the field.
    }
  };

  return (
    <div className="space-y-2 text-left">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {t('publicBooking.book.linkHint')}
      </p>
      <div className="flex gap-2">
        <input
          readOnly
          value={url}
          aria-label={t('publicBooking.book.linkLabel')}
          onFocus={event => event.currentTarget.select()}
          className="min-w-0 flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
        />
        <Button type="button" variant="outline" onClick={copy}>
          {copied ? t('publicBooking.book.linkCopied') : t('publicBooking.book.linkCopy')}
        </Button>
      </div>
    </div>
  );
};

interface ChoiceButtonProps {
  selected: boolean;
  onClick: () => void;
  title: string;
  detail?: string;
}

const ChoiceButton: React.FC<ChoiceButtonProps> = ({ selected, onClick, title, detail }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={`text-left rounded-lg border p-3 transition-colors ${
      selected
        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
        : 'border-gray-200 dark:border-gray-700 hover:border-primary-300'
    }`}
  >
    <span className="block text-sm font-semibold text-gray-900 dark:text-white">{title}</span>
    {detail && <span className="block text-xs text-gray-500 dark:text-gray-400">{detail}</span>}
  </button>
);

const BookingPageView: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const language: 'de' | 'en' = locale.startsWith('en') ? 'en' : 'de';

  const [pageState, setPageState] = useState<PageState>('loading');
  const [page, setPage] = useState<PublicBookingPage | null>(null);

  const [serviceId, setServiceId] = useState('');
  const [optionChosen, setOptionChosen] = useState(false);
  const [variantId, setVariantId] = useState('');
  const [resourceId, setResourceId] = useState('');
  const [dateFrom, setDateFrom] = useState('');

  const [slots, setSlots] = useState<PublicSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState<PublicSlot | null>(null);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState<PublicBooking | null>(null);

  const slotRequestId = useRef(0);

  const loadPage = useCallback(async () => {
    setPageState('loading');
    try {
      const data = await publicBookingService.getBookingPage(slug);
      setPage(data);
      setDateFrom(
        formatInTimezone(new Date().toISOString(), data.timezone, 'sv-SE', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })
      );
      setPageState('ready');
    } catch (error) {
      setPageState(
        error instanceof PublicApiError && error.status === 404 ? 'unavailable' : 'error'
      );
    }
  }, [slug]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  const service: PublicService | undefined = useMemo(
    () => page?.services.find(item => item.id === serviceId),
    [page, serviceId]
  );
  const variant = service?.variants.find(item => item.id === variantId);

  // A service without variants needs no option step
  const searchReady = Boolean(service) && (optionChosen || service?.variants.length === 0);

  useEffect(() => {
    if (!page || !service || !searchReady || !dateFrom) return;
    const requestId = ++slotRequestId.current;
    setSlotsLoading(true);
    setSlotsError(false);
    publicBookingService
      .getSlots(slug, {
        service: service.id,
        variant: variantId || undefined,
        resource: resourceId || undefined,
        dateFrom,
        days: SLOT_WINDOW_DAYS,
      })
      .then(response => {
        if (requestId === slotRequestId.current) setSlots(response.slots);
      })
      .catch(() => {
        if (requestId === slotRequestId.current) {
          setSlots([]);
          setSlotsError(true);
        }
      })
      .finally(() => {
        if (requestId === slotRequestId.current) setSlotsLoading(false);
      });
  }, [page, service, searchReady, slug, variantId, resourceId, dateFrom, reloadKey]);

  const today = useMemo(
    () =>
      page
        ? formatInTimezone(new Date().toISOString(), page.timezone, 'sv-SE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })
        : '',
    [page]
  );
  const lastDay = page ? addDays(today, page.max_advance_days) : '';

  const slotsByDay = useMemo(() => {
    if (!page) return [];
    const groups = new Map<string, PublicSlot[]>();
    for (const slot of slots) {
      const key = formatInTimezone(slot.start, page.timezone, 'sv-SE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      groups.set(key, [...(groups.get(key) ?? []), slot]);
    }
    return Array.from(groups.entries());
  }, [page, slots]);

  const resetSelection = () => {
    setSelectedSlot(null);
    setSubmitError('');
    setFieldErrors({});
  };

  const chooseService = (id: string) => {
    setServiceId(id);
    setVariantId('');
    setOptionChosen(false);
    setResourceId('');
    setSlots([]);
    resetSelection();
  };

  const chooseVariant = (id: string) => {
    setVariantId(id);
    setOptionChosen(true);
    resetSelection();
  };

  const chooseResource = (id: string) => {
    setResourceId(id);
    resetSelection();
  };

  const changeDateFrom = (value: string) => {
    if (!value) return;
    setDateFrom(value < today ? today : value);
    resetSelection();
  };

  const timeLabel = (slot: PublicSlot) =>
    formatInTimezone(slot.start, page?.timezone ?? 'UTC', locale, {
      hour: '2-digit',
      minute: '2-digit',
    });

  const price = (value: string | null) =>
    value === null
      ? undefined
      : new Intl.NumberFormat(locale, { style: 'currency', currency: CURRENCY }).format(
          Number(value)
        );

  const detailLine = (minutes: number, value: string | null) =>
    [formatDuration(minutes), price(value)].filter(Boolean).join(' · ');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!service || !selectedSlot) return;

    const errors: Record<string, string> = {};
    if (!name.trim()) errors.customer_name = t('publicBooking.book.required');
    if (!phone.trim()) errors.customer_phone = t('publicBooking.book.required');
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    setSubmitError('');
    try {
      const booking = await publicBookingService.createBooking(slug, {
        service: service.id,
        variant: variantId || undefined,
        resource: resourceId || undefined,
        start: selectedSlot.start,
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        customer_email: email.trim() || undefined,
        language,
      });
      setConfirmed(booking);
    } catch (error) {
      if (error instanceof PublicApiError && error.status === 409) {
        setSubmitError(t('publicBooking.book.slotTaken'));
        setSelectedSlot(null);
        setReloadKey(key => key + 1);
      } else if (error instanceof PublicApiError && error.code === 'start_in_past') {
        // The page was open for a while and the chosen time has passed.
        setSubmitError(t('publicBooking.book.startInPast'));
        setSelectedSlot(null);
        setReloadKey(key => key + 1);
      } else if (error instanceof PublicApiError && error.status === 400) {
        const next = { ...error.fieldErrors };
        if (next.customer_phone) next.customer_phone = t('publicBooking.book.phoneInvalid');
        setFieldErrors(next);
        if (Object.keys(next).length === 0) setSubmitError(t('publicBooking.book.errorText'));
        if (next.start) {
          setSelectedSlot(null);
          setReloadKey(key => key + 1);
        }
      } else if (error instanceof PublicApiError && error.status === 429) {
        setSubmitError(t('publicBooking.book.tooManyRequests'));
      } else if (error instanceof PublicApiError && error.status === 404) {
        setPageState('unavailable');
      } else {
        setSubmitError(t('publicBooking.book.errorText'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (pageState === 'loading') {
    return (
      <PublicBookingLayout>
        <Loader message={t('publicBooking.loading')} size="md" />
      </PublicBookingLayout>
    );
  }

  if (pageState === 'unavailable' || pageState === 'error' || !page) {
    const unavailable = pageState === 'unavailable';
    return (
      <PublicBookingLayout>
        <Card className="shadow-2xl">
          <CardHeader className="text-center pb-8">
            <StatusIcon icon={unavailable ? 'event_busy' : 'error_outline'} tone="error" />
            <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">
              {unavailable
                ? t('publicBooking.book.unavailableTitle')
                : t('publicBooking.book.title')}
            </CardTitle>
            <CardDescription className="text-gray-600 dark:text-gray-400">
              {unavailable
                ? t('publicBooking.book.unavailableText')
                : t('publicBooking.book.errorText')}
            </CardDescription>
          </CardHeader>
          {!unavailable && (
            <CardContent className="px-8 pb-8">
              <Button variant="gradient-multi" size="lg" className="w-full" onClick={loadPage}>
                {t('publicBooking.manage.retry')}
              </Button>
            </CardContent>
          )}
        </Card>
      </PublicBookingLayout>
    );
  }

  if (confirmed) {
    const date = formatInTimezone(confirmed.start, confirmed.timezone, locale, {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
    const time = formatInTimezone(confirmed.start, confirmed.timezone, locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
    return (
      <PublicBookingLayout>
        <Card className="shadow-2xl">
          <CardHeader className="text-center pb-6">
            <StatusIcon icon="check" tone="success" />
            <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('publicBooking.book.successTitle')}
            </CardTitle>
            <CardDescription className="text-gray-600 dark:text-gray-400">
              {t('publicBooking.book.successText')}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-8 pb-8 space-y-6 text-center">
            <div className="space-y-1">
              <p className="font-semibold text-gray-900 dark:text-white">
                {confirmed.profile_name}
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                {confirmed.service_name}
                {confirmed.variant_name ? ` (${confirmed.variant_name})` : ''}
                {confirmed.resource_name
                  ? ` ${t('publicBooking.book.withResource', { name: confirmed.resource_name })}`
                  : ''}
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                {date}, {time}
              </p>
            </div>
            <Button
              variant="gradient-multi"
              size="lg"
              className="w-full"
              onClick={() => navigate(`/manage/${encodeURIComponent(confirmed.manage_token)}`)}
            >
              {t('publicBooking.book.manageLink')}
            </Button>
            <ManageLinkBox
              url={`${window.location.origin}/manage/${encodeURIComponent(confirmed.manage_token)}`}
            />
          </CardContent>
        </Card>
      </PublicBookingLayout>
    );
  }

  const selectedResourceName =
    selectedSlot?.resources.length === 1 ? selectedSlot.resources[0].name : '';

  return (
    <PublicBookingLayout maxWidth="2xl">
      <Card className="shadow-2xl">
        <CardHeader className="text-center pb-6">
          <StatusIcon icon="event" />
          <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('publicBooking.book.title')}
          </CardTitle>
          <CardDescription className="text-lg text-gray-600 dark:text-gray-400">
            {page.name}
          </CardDescription>
        </CardHeader>

        <CardContent className="px-6 sm:px-8 pb-8 space-y-8">
          <section className="space-y-3">
            <SectionTitle step={1}>{t('publicBooking.book.stepService')}</SectionTitle>
            <div className="grid gap-2 sm:grid-cols-2">
              {page.services.map(item => (
                <ChoiceButton
                  key={item.id}
                  selected={item.id === serviceId}
                  onClick={() => chooseService(item.id)}
                  title={item.name}
                  detail={detailLine(item.duration_minutes, item.price)}
                />
              ))}
            </div>
          </section>

          {service && service.variants.length > 0 && (
            <section className="space-y-3">
              <SectionTitle step={2}>{t('publicBooking.book.stepVariant')}</SectionTitle>
              <div className="grid gap-2 sm:grid-cols-2">
                <ChoiceButton
                  selected={optionChosen && variantId === ''}
                  onClick={() => chooseVariant('')}
                  title={t('publicBooking.book.standardOption')}
                  detail={detailLine(service.duration_minutes, service.price)}
                />
                {service.variants.map(item => (
                  <ChoiceButton
                    key={item.id}
                    selected={item.id === variantId}
                    onClick={() => chooseVariant(item.id)}
                    title={item.name}
                    detail={detailLine(item.duration_minutes, item.price ?? service.price)}
                  />
                ))}
              </div>
            </section>
          )}

          {service && searchReady && (
            <>
              {service.resources.length > 1 && (
                <section className="space-y-3">
                  <SectionTitle step={service.variants.length > 0 ? 3 : 2}>
                    {t('publicBooking.book.stepResource')}
                  </SectionTitle>
                  <div className="grid gap-2 sm:grid-cols-3">
                    <ChoiceButton
                      selected={resourceId === ''}
                      onClick={() => chooseResource('')}
                      title={t('publicBooking.book.anyResource')}
                    />
                    {service.resources.map(item => (
                      <ChoiceButton
                        key={item.id}
                        selected={item.id === resourceId}
                        onClick={() => chooseResource(item.id)}
                        title={item.name}
                      />
                    ))}
                  </div>
                </section>
              )}

              <section className="space-y-3">
                <SectionTitle
                  step={
                    (service.variants.length > 0 ? 3 : 2) + (service.resources.length > 1 ? 1 : 0)
                  }
                >
                  {t('publicBooking.book.stepSlot')}
                </SectionTitle>

                <div className="flex flex-wrap items-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={dateFrom <= today}
                    onClick={() => changeDateFrom(addDays(dateFrom, -SLOT_WINDOW_DAYS))}
                  >
                    {t('publicBooking.book.earlier')}
                  </Button>
                  <Input
                    id="booking-date-from"
                    type="date"
                    label={t('publicBooking.book.dateFrom')}
                    value={dateFrom}
                    min={today}
                    max={lastDay}
                    onChange={event => changeDateFrom(event.target.value)}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={addDays(dateFrom, SLOT_WINDOW_DAYS) > lastDay}
                    onClick={() => changeDateFrom(addDays(dateFrom, SLOT_WINDOW_DAYS))}
                  >
                    {t('publicBooking.book.later')}
                  </Button>
                </div>

                {slotsLoading && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('publicBooking.loading')}
                  </p>
                )}
                {slotsError && (
                  <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                    {t('publicBooking.book.errorText')}
                  </p>
                )}
                {!slotsLoading && !slotsError && slotsByDay.length === 0 && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('publicBooking.book.noSlots')}
                  </p>
                )}

                <div className="space-y-4">
                  {slotsByDay.map(([day, daySlots]) => (
                    <div key={day}>
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        {formatInTimezone(daySlots[0].start, page.timezone, locale, {
                          weekday: 'long',
                          day: '2-digit',
                          month: 'long',
                        })}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {daySlots.map(slot => (
                          <button
                            key={slot.start}
                            type="button"
                            aria-pressed={selectedSlot?.start === slot.start}
                            onClick={() => {
                              setSelectedSlot(slot);
                              setSubmitError('');
                            }}
                            className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                              selectedSlot?.start === slot.start
                                ? 'border-primary-500 bg-primary-500 text-white'
                                : 'border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white hover:border-primary-300'
                            }`}
                          >
                            {timeLabel(slot)}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}

          {submitError && !selectedSlot && (
            <div
              role="alert"
              className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3"
            >
              <p className="text-sm text-red-600 dark:text-red-400">{submitError}</p>
            </div>
          )}

          {service && selectedSlot && (
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <SectionTitle
                step={
                  (service.variants.length > 0 ? 3 : 2) + (service.resources.length > 1 ? 2 : 1)
                }
              >
                {t('publicBooking.book.stepDetails')}
              </SectionTitle>

              <p className="text-sm text-gray-600 dark:text-gray-400">
                {service.name}
                {variant ? ` (${variant.name})` : ''}
                {selectedResourceName
                  ? ` ${t('publicBooking.book.withResource', { name: selectedResourceName })}`
                  : ''}
                {' · '}
                {formatInTimezone(selectedSlot.start, page.timezone, locale, {
                  weekday: 'long',
                  day: '2-digit',
                  month: 'long',
                })}
                , {timeLabel(selectedSlot)}
              </p>

              <Input
                id="booking-name"
                label={`${t('publicBooking.book.name')} *`}
                value={name}
                onChange={event => setName(event.target.value)}
                errorText={fieldErrors.customer_name}
                autoComplete="name"
                fullWidth
                disabled={submitting}
              />
              <Input
                id="booking-phone"
                type="tel"
                label={`${t('publicBooking.book.phone')} *`}
                helperText={t('publicBooking.book.phoneHint')}
                value={phone}
                onChange={event => setPhone(event.target.value)}
                errorText={fieldErrors.customer_phone}
                autoComplete="tel"
                fullWidth
                disabled={submitting}
              />
              <Input
                id="booking-email"
                type="email"
                label={t('publicBooking.book.email')}
                value={email}
                onChange={event => setEmail(event.target.value)}
                errorText={fieldErrors.customer_email}
                autoComplete="email"
                fullWidth
                disabled={submitting}
              />

              {submitError && (
                <div
                  role="alert"
                  className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3"
                >
                  <p className="text-sm text-red-600 dark:text-red-400">{submitError}</p>
                </div>
              )}

              <Button
                type="submit"
                variant="gradient-multi"
                size="lg"
                className="w-full"
                loading={submitting}
                disabled={submitting}
              >
                {submitting ? t('publicBooking.book.submitting') : t('publicBooking.book.submit')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </PublicBookingLayout>
  );
};

export { BookingPageView as BookingPage };
